import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { setClientTimeZone, getClientTimeZone, fmtInZone } from '../../utils/datetime';
import { pageTitles, pageGroups } from '../../layouts/navData';
import { LoaderSymbol, LOADER_SYMBOLS, setLoaderSymbol } from '../../components/loading';

type LoaderSym = (typeof LOADER_SYMBOLS)[number];

// ─────────────────────────────────────────────────────────────────────────
// Settings → App Config
// ─────────────────────────────────────────────────────────────────────────
//   App-wide configuration the admin edits live (no API restart):
//     1. Client timezone — the IANA zone the lab's still-Central (XAF-shared)
//        tables are stored in.  Used by the shared fmtLocal* helpers to convert
//        those tables' naive (mode-b) wire values to each viewer's local time.
//     2. Per-form date source — override how an individual form interprets its
//        stored dates: Auto (default — the wire's 'Z' marker decides), UTC, or
//        Client.  Applied centrally in AppShell as each form opens.
//
//   MIGRATION-TEMPORARY (ADR-0008): when every table is UTC every value carries
//   a 'Z', the client-zone + per-form override become unused, and this page's
//   timezone/selector sections are removed in one place.  See CLAUDE.md §14b.6.
//
//   Persisted to dbo.AI_AppSettings (keys 'ClientTimeZone', 'FormDateModes'),
//   surfaced via GET /api/v1/app-config, audited as "App Config".
// ─────────────────────────────────────────────────────────────────────────

type Mode = 'auto' | 'utc' | 'client';

/** Full IANA zone list from the browser, with a small fallback for older engines. */
function ianaZones(): string[] {
  const sv = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof sv === 'function') {
    try { return sv('timeZone'); } catch { /* fall through */ }
  }
  return [
    'America/Chicago', 'America/New_York', 'America/Denver', 'America/Los_Angeles',
    'America/Phoenix', 'UTC', 'Europe/London', 'Europe/Paris', 'Asia/Kolkata',
    'Asia/Singapore', 'Asia/Dubai', 'Australia/Sydney',
  ];
}

/** Every selectable form (from the nav catalog), excluding Home and this page. */
function formCatalog(): Array<{ id: string; label: string; group: string }> {
  return Object.entries(pageTitles)
    .filter(([id]) => id !== 'home' && id !== 'appconfig')
    .map(([id, label]) => ({ id, label, group: pageGroups[id] ?? '' }))
    .sort((a, b) => `${a.group} ${a.label}`.localeCompare(`${b.group} ${b.label}`));
}

export default function AppConfigPage() {
  const notify = useNotify();
  // ── View-then-Edit gate (BO UI Standard v2) — the SHARED config-panel flow ─
  // The page opens READ-ONLY; the toolbar Edit button unlocks the controls;
  // Save persists everything in one audited batch (shared useSaveFlow); Cancel
  // reloads from the server, discarding edits.
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [zone, setZone] = useState<string>(getClientTimeZone());
  const [initialZone, setInitialZone] = useState<string>(getClientTimeZone());
  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [initialModes, setInitialModes] = useState<Record<string, Mode>>({});
  const [symbol, setSymbol] = useState<LoaderSym>('ring');
  const [initialSymbol, setInitialSymbol] = useState<LoaderSym>('ring');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tick, setTick] = useState(0);

  const zones = useMemo(ianaZones, []);
  const viewerZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'local', []);
  const catalog = useMemo(formCatalog, []);
  const now = useMemo(() => new Date(), [tick]); // live preview clock (re-reads each tick)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter((f) => f.label.toLowerCase().includes(q) || f.group.toLowerCase().includes(q));
  }, [catalog, search]);
  const overrideCount = Object.keys(modes).length;

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  /** Load the live config (client timezone + per-form modes + loader symbol) —
   *  reused by the toolbar Refresh and by Cancel (discard edits → reload). */
  const load = () => {
    setLoading(true); setErrMsg(null);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      controller.abort();
      setErrMsg('Timed out after 10 s. The API may be unreachable — check the server is running and your session is valid.');
      setLoading(false);
    }, 10_000);

    return api.get('/api/v1/app-config', { signal: controller.signal })
      .then((res) => {
        const d = (res.data as {
          data?: { clientTimeZone?: string; formDateModes?: Record<string, string>; loaderSymbol?: string };
        })?.data;
        if (d?.clientTimeZone) { setZone(d.clientTimeZone); setInitialZone(d.clientTimeZone); }
        const m = (d?.formDateModes ?? {}) as Record<string, Mode>;
        setModes(m); setInitialModes(m);
        const ls = d?.loaderSymbol;
        if (ls && (LOADER_SYMBOLS as readonly string[]).includes(ls)) {
          setSymbol(ls as LoaderSym); setInitialSymbol(ls as LoaderSym);
        }
      })
      .catch((e) => {
        if ((e as { code?: string }).code === 'ERR_CANCELED') return;
        const status = (e as { response?: { status?: number } }).response?.status;
        if (status === 403) setErrMsg('You do not have permission to view this setting.');
        else setErrMsg((e as { message?: string }).message ?? 'Failed to load.');
      })
      .finally(() => { window.clearTimeout(timer); setLoading(false); });
  };
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const setFormMode = (id: string, mode: Mode) =>
    setModes((prev) => {
      const next = { ...prev };
      if (mode === 'auto') delete next[id]; else next[id] = mode; // 'auto' clears the override
      return next;
    });

  /** Persist EVERYTHING that changed in one audited batch (the shared Save). */
  const persistAll = async () => {
    // 1. Client timezone (only if changed).
    if (zone !== initialZone) {
      const res = await api.put('/api/v1/app-config/client-timezone', { timeZone: zone });
      const saved = (res.data as { data?: { clientTimeZone?: string } })?.data?.clientTimeZone ?? zone;
      setZone(saved); setInitialZone(saved); setClientTimeZone(saved);
    }
    // 2. Per-form overrides that changed (including cleared → 'auto').
    const ids = new Set<string>([...Object.keys(modes), ...Object.keys(initialModes)]);
    for (const id of ids) {
      const cur: Mode = modes[id] ?? 'auto';
      const init: Mode = initialModes[id] ?? 'auto';
      if (cur !== init) {
        await api.put('/api/v1/app-config/form-date-mode', { formId: id, mode: cur });
      }
    }
    setInitialModes({ ...modes });
    // 3. Global loading symbol (only if changed) — applies app-wide now.
    if (symbol !== initialSymbol) {
      const res = await api.put('/api/v1/app-config/loader-symbol', { symbol });
      const saved = ((res.data as { data?: { loaderSymbol?: string } })?.data?.loaderSymbol ?? symbol) as LoaderSym;
      setSymbol(saved); setInitialSymbol(saved); setLoaderSymbol(saved);
    }
  };

  /** Toolbar Save — the shared view-then-edit save (confirm → audited batch →
   *  "Saved successfully." → back to read-only). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'App Config',
      successMessage: 'App Config saved successfully.',
      save: persistAll,
      reload: load,
      onError: (e: unknown) => notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
        ?? (e as { message?: string }).message
        ?? 'Save failed.'),
    }).finally(() => setSaving(false));
  };

  /** Cancel — discard edits by reloading the live config from the server. */
  const handleCancel = () => gate.cancel(() => void load());

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--ai-muted)', fontSize: 13 }}>
        <LoaderSymbol size={14} /> Loading settings…
      </div>
    );
  }

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--ai-ink)', display: 'block', marginBottom: 4 };
  const input: React.CSSProperties = { padding: '6px 10px', fontSize: 13, border: '1px solid var(--ai-line)', borderRadius: 4, width: '100%', maxWidth: 380, boxSizing: 'border-box' };
  // Class F panel card — white on the grey page bg (BO Standard).
  const section: React.CSSProperties = { background: 'var(--ai-card, #fff)', border: '1px solid var(--ai-line)', borderRadius: 10, boxShadow: '0 1px 3px rgba(20,24,40,.05)', padding: '14px 18px', marginBottom: 14 };
  const modeSel: React.CSSProperties = { padding: '4px 8px', fontSize: 12.5, border: '1px solid var(--ai-line)', borderRadius: 4, background: '#fff' };

  return (
    <div className="dt-scroll" style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', background: 'var(--ai-bg)', fontFamily: 'var(--ai-font)' }}>
      <style>{`
        .dt-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
        .dt-scroll::-webkit-scrollbar-track { background: transparent; }
        .dt-scroll::-webkit-scrollbar-thumb { background: var(--ai-line); border-radius: 5px; }
        .dt-scroll::-webkit-scrollbar-thumb:hover { background: var(--ai-muted); }
      `}</style>
      <div style={{ padding: '14px 20px' }}>
        {/* Shared title (nav icon/colour + load-time) — single source of truth. */}
        <FormTitle pageId="appconfig" auditFormName="App Config" />
        <div style={{ fontSize: 12, color: 'var(--ai-muted)', marginBottom: 14, maxWidth: 760 }}>
          App-wide settings, edited live. The <strong>client timezone</strong> is the zone
          the lab's not-yet-migrated tables store dates in; the app shows those dates in each
          viewer's own local time. Migrated (UTC) screens ignore it.
        </div>

        {errMsg && (
          <div style={{ background: 'var(--ai-pill-alert-bg)', border: '1px solid var(--ai-pill-alert-fg)', color: 'var(--ai-pill-alert-fg)', padding: '10px 14px', borderRadius: 4, fontSize: 12, marginBottom: 14, maxWidth: 760 }}>
            <i className="ti ti-alert-triangle" />&nbsp;{errMsg}
          </div>
        )}

        {/* Standard action bar (§18.6): view-then-Edit (shared EditGateActions —
            ✎ Edit → 💾 Save / ↩︎ Cancel) · Refresh.  The page is READ-ONLY
            until Edit; Save persists everything that changed in one audited
            batch; Cancel reloads from the server, discarding edits. */}
        <div style={{ margin: '0 0 12px' }}>
          <AppToolbar
            left={
              <EditGateActions
                gate={gate}
                saving={saving}
                disabled={loading}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            }
            onRefresh={() => void load()}
            refreshing={loading}
            refreshDisabled={gate.editing}
          />
        </div>

        {/* 1 — Client timezone */}
        <div style={section}>
          <label style={label} htmlFor="ac-zone">Client timezone</label>
          <select id="ac-zone" style={input} value={zone} disabled={ro} onChange={(e) => setZone(e.target.value)}>
            {!zones.includes(zone) && <option value={zone}>{zone}</option>}
            {zones.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ai-muted)' }}>
            {zones.length} time zones · default <code>America/Chicago</code> (Houston / Central).
          </div>
        </div>

        {/* Live preview */}
        <div style={section}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: 'var(--ai-blue)' }}>
            <i className="ti ti-eye" style={{ marginRight: 6 }} /> Preview (right now)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,220px) 1fr', gap: '6px 16px', fontSize: 13, alignItems: 'center' }}>
            <span style={{ color: 'var(--ai-muted)' }}>Selected client zone</span>
            <strong>{fmtInZone(now, zone)} <span style={{ fontWeight: 400, color: 'var(--ai-muted)' }}>({zone})</span></strong>
            <span style={{ color: 'var(--ai-muted)' }}>Your local zone</span>
            <strong>{fmtInZone(now, viewerZone)} <span style={{ fontWeight: 400, color: 'var(--ai-muted)' }}>({viewerZone})</span></strong>
          </div>
        </div>

        {/* 2 — Per-form date source */}
        <div style={section}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ai-blue)' }}>
              <i className="ti ti-forms" style={{ marginRight: 6 }} /> Per-form date source
              <span style={{ fontWeight: 400, color: 'var(--ai-muted)', marginLeft: 8 }}>
                ({overrideCount} override{overrideCount === 1 ? '' : 's'})
              </span>
            </div>
            <div className="list-search" style={{ width: 240, padding: '5px 10px' }}>
              <i className="ti ti-search" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search forms…"
              />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 10 }}>
            <strong>Auto</strong> (default) decides from the data; <strong>UTC</strong> forces UTC→local;
            <strong> Client</strong> forces client-zone→local. Leave forms on Auto unless one shows the wrong time.
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--ai-line)', borderRadius: 5 }}>
            {filtered.map((f, i) => {
              const v: Mode = modes[f.id] ?? 'auto';
              return (
                <div key={f.id} style={{
                  display: 'grid', gridTemplateColumns: '1fr 140px', gap: 12, alignItems: 'center',
                  padding: '7px 12px', borderBottom: i === filtered.length - 1 ? 'none' : '1px solid var(--ai-line-soft)',
                  background: v === 'auto' ? '#fff' : '#fff8f1',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--ai-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</div>
                    {f.group && <div style={{ fontSize: 10.5, color: 'var(--ai-muted)' }}>{f.group}</div>}
                  </div>
                  <select style={modeSel} value={v} disabled={ro} onChange={(e) => setFormMode(f.id, e.target.value as Mode)}>
                    <option value="auto">Auto</option>
                    <option value="utc">UTC</option>
                    <option value="client">Client</option>
                  </select>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--ai-muted)', textAlign: 'center' }}>No forms match “{search}”.</div>
            )}
          </div>
        </div>

        {/* 3 — Global loading indicator (§18.6) */}
        <div style={section}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--ai-blue)' }}>
            <i className="ti ti-loader" style={{ marginRight: 6 }} /> Loading indicator
          </div>
          <div style={{ fontSize: 11, color: 'var(--ai-muted)', marginBottom: 10 }}>
            The animation shown for every loading state app-wide — data retrieve, save, validate,
            export, report. Pick one; it applies to all users immediately.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 10, maxWidth: 560 }}>
            {LOADER_SYMBOLS.map((key) => {
              const active = symbol === key;
              return (
                <button
                  key={key} type="button" onClick={() => { if (!ro) setSymbol(key); }} disabled={ro}
                  aria-pressed={active}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                    padding: '16px 8px 10px', cursor: ro ? 'default' : 'pointer',
                    background: active ? 'var(--ai-orange-tint)' : '#fff',
                    border: active ? '2px solid var(--brand-accent, var(--ai-orange))' : '1px solid var(--ai-line)',
                    borderRadius: 6,
                  }}
                >
                  <span style={{ height: 30, display: 'inline-flex', alignItems: 'center' }}>
                    <LoaderSymbol symbol={key} size={26} />
                  </span>
                  <span style={{ fontSize: 12, fontFamily: 'monospace', color: active ? 'var(--ai-orange-hover)' : 'var(--ai-ink)' }}>{key}</span>
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ai-muted)' }}>
            Default <code>ring</code>. Colour follows the theme — no per-form spinners.
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 11, color: 'var(--ai-muted)' }}>
          Applies to every user; takes effect immediately. Press <b>Edit</b> above to change these settings.
        </div>
      </div>
    </div>
  );
}
